// The week-by-week grid behind a meso's Mileage and Strength percentages.
//
// /api/scores/activity-calendar returns each workout already classified the
// way the scoring pipeline counts it (miles, strength credit). This lays those
// workouts out as Monday–Sunday weeks, adds them up, and says whether the sum
// still matches the published score — FinalSurge rows can arrive or change
// after the pipeline last ran.

import { toOneDecimal } from './activitySummary';

export interface PlannedWorkout {
  date: string;
  activity: string;
  name: string | null;
  // Counted toward planned mileage; null for activities that never are.
  miles: number | null;
  strength: boolean;
}

export interface CompletedWorkout extends PlannedWorkout {
  // As logged, for display; `miles` is what the score credits.
  distance: number | null;
  unit: string | null;
}

export interface OfficialTotals {
  plannedMiles: number;
  completedMiles: number;
  plannedStrength: number;
  completedStrength: number;
  updatedAt: string | null;
}

export interface MesoActivity {
  meso: string;
  start: string;
  end: string;
  official: OfficialTotals | null;
  planned: PlannedWorkout[];
  completed: CompletedWorkout[];
}

export type DayStatus = 'done' | 'partial' | 'missed' | 'extra' | 'rest';

export interface CalendarDay {
  date: string;
  planned: PlannedWorkout[];
  completed: CompletedWorkout[];
  plannedMiles: number;
  completedMiles: number;
  plannedStrength: number;
  completedStrength: number;
  status: DayStatus;
}

export interface CalendarWeek {
  days: CalendarDay[];
  // The pipeline only scores weeks the cohort has a plan for.
  hasPlan: boolean;
}

export interface MesoCalendar {
  weeks: CalendarWeek[];
  totals: {
    plannedMiles: number;
    completedMiles: number;
    plannedStrength: number;
    completedStrength: number;
    mileagePercent: number | null;
    strengthPercent: number | null;
  };
  hasPlan: boolean;
  differsFromOfficial: boolean;
}

// GPS watches routinely log a planned 3 mile route as 2.9.
const DONE_SHARE = 0.95;

const addDays = (date: string, days: number): string => {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const dateParts = (date: string) => {
  const d = new Date(`${date}T00:00:00Z`);
  return {
    month: d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }),
    day: d.getUTCDate(),
    year: d.getUTCFullYear(),
  };
};

// "Aug 3 – 23, 2026", "Aug 24 – Sep 13, 2026", "Dec 21, 2026 – Jan 10, 2027"
export function mesoDateRange(start: string, end: string): string {
  const s = dateParts(start);
  const e = dateParts(end);
  if (s.year !== e.year) return `${s.month} ${s.day}, ${s.year} – ${e.month} ${e.day}, ${e.year}`;
  if (s.month !== e.month) return `${s.month} ${s.day} – ${e.month} ${e.day}, ${e.year}`;
  return `${s.month} ${s.day} – ${e.day}, ${e.year}`;
}

// A grid cell's date: the month is named on the grid's first day and on each
// 1st, so a meso that crosses months reads unambiguously.
export function dayLabel(date: string, indexInGrid: number): string {
  const { month, day } = dateParts(date);
  return indexInGrid === 0 || day === 1 ? `${month} ${day}` : String(day);
}

const sumMiles = (workouts: PlannedWorkout[]) =>
  workouts.reduce((total, w) => total + (w.miles ?? 0), 0);
const countStrength = (workouts: PlannedWorkout[]) =>
  workouts.filter(w => w.strength).length;

function dayStatus(planned: PlannedWorkout[], completed: CompletedWorkout[]): DayStatus {
  const plannedMiles = sumMiles(planned);
  const completedMiles = sumMiles(completed);
  const plannedStrength = countStrength(planned);
  const completedStrength = countStrength(completed);
  const plansRunning = planned.some(w => w.miles !== null);
  const loggedRunning = completed.some(w => w.miles !== null && w.miles > 0);
  const countedWork = loggedRunning || completedStrength > 0;

  if (!plansRunning && plannedStrength === 0) return countedWork ? 'extra' : 'rest';

  const milesMet = !plansRunning ||
    (plannedMiles > 0 ? completedMiles >= plannedMiles * DONE_SHARE : loggedRunning);
  const strengthMet = completedStrength >= plannedStrength;

  if (milesMet && strengthMet) return 'done';
  return countedWork ? 'partial' : 'missed';
}

const percent = (completed: number, planned: number) =>
  planned > 0 ? toOneDecimal((completed / planned) * 100) : null;

export function buildMesoCalendar(meso: MesoActivity): MesoCalendar {
  const weeks: CalendarWeek[] = [];
  for (let weekStart = meso.start; weekStart <= meso.end; weekStart = addDays(weekStart, 7)) {
    const days: CalendarDay[] = Array.from({ length: 7 }, (_, i) => {
      const date = addDays(weekStart, i);
      const planned = meso.planned.filter(w => w.date === date);
      const completed = meso.completed.filter(w => w.date === date);
      return {
        date,
        planned,
        completed,
        plannedMiles: toOneDecimal(sumMiles(planned)),
        completedMiles: toOneDecimal(sumMiles(completed)),
        plannedStrength: countStrength(planned),
        completedStrength: countStrength(completed),
        status: dayStatus(planned, completed),
      };
    });
    weeks.push({ days, hasPlan: days.some(d => d.planned.length > 0) });
  }

  const scored = weeks.filter(w => w.hasPlan).flatMap(w => w.days);
  const plannedMiles = toOneDecimal(sumMiles(meso.planned));
  const completedMiles = toOneDecimal(sumMiles(scored.flatMap(d => d.completed)));
  const plannedStrength = countStrength(meso.planned);
  const completedStrength = countStrength(scored.flatMap(d => d.completed));

  const { official } = meso;
  const differsFromOfficial = official !== null && (
    Math.abs(official.plannedMiles - plannedMiles) >= 0.05 ||
    Math.abs(official.completedMiles - completedMiles) >= 0.05 ||
    official.plannedStrength !== plannedStrength ||
    official.completedStrength !== completedStrength
  );

  return {
    weeks,
    totals: {
      plannedMiles,
      completedMiles,
      plannedStrength,
      completedStrength,
      mileagePercent: percent(completedMiles, plannedMiles),
      strengthPercent: percent(completedStrength, plannedStrength),
    },
    hasPlan: meso.planned.length > 0,
    differsFromOfficial,
  };
}

const mesoNumber = (label: string) => parseInt(label.replace(/\D/g, ''), 10) || 0;

// The mesos whose percentages can be explained — those with a plan — newest first.
export function breakdownMesos(mesos: MesoActivity[]): Array<{ meso: MesoActivity; calendar: MesoCalendar }> {
  return mesos
    .map(meso => ({ meso, calendar: buildMesoCalendar(meso) }))
    .filter(({ calendar }) => calendar.hasPlan)
    .sort((a, b) => mesoNumber(b.meso.meso) - mesoNumber(a.meso.meso));
}
